/** @jsxRuntime classic */
import React, { lazy, Suspense, useCallback } from 'react';

import { createRecommendationContextExtensions } from '@atlassiansox/cross-flow-extensions';

import { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type Journey, Journeys } from '@atlassian/app-provisioning';
import { type SurfaceModule } from '@atlassian/surface-sdk';

import { type Actions } from '../../../index';
import { formatStringMessage } from '../../../utils/formatMessage';
import {
	getExperimentCohort,
	getRecommendationDescription,
} from '../app-recommendations-list/experiment';
import { type AppRecommendationsProps } from '../types';

import { AppLogo } from './app-logo';
import { getExperimentValues } from './experiment';
import { type AppProvisionProps, type AppRecommendationProps } from './types';

const AppRecommendationMenu = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_app-recommendation-menu" */ '../app-recommendation-menu'
		),
);

const recommendationStyles = xcss({
	display: 'flex',
	width: '100%',
	justifyItems: 'stretch',
	justifyContent: 'flex-end',
	gap: 'space.025',
	height: '48px',
	alignItems: 'center',
});

const commonButtonStyles = xcss({
	borderRadius: 'border.radius',
	border: 'none',
	display: 'flex',
	backgroundColor: 'color.background.neutral.subtle',
	minWidth: '0',
	height: token('space.600'),
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const recommendationButtonStyles = xcss({
	textAlign: 'left',
	flexGrow: 1,
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	lineHeight: '18px',
	alignItems: 'stretch',
	color: 'color.text',
});

const menuTriggerButtonStyles = xcss({
	width: token('space.600'),
	padding: 'space.0',
	alignItems: 'center',
	justifyContent: 'center',
});

const recommendationBodyIconStyles = xcss({
	marginRight: 'space.100',
	display: 'flex',
	alignItems: 'center',
});

const recommendationBodyContentStyles = xcss({
	overflow: 'hidden',
	fontWeight: 400,
	display: 'flex',
	flexDirection: 'column',
});

const titleStyles = xcss({
	fontSize: token('font.body', '14px'),
	fontWeight: 400,
	marginTop: 'space.025',
	minWidth: '0',
	lineHeight: '16px',
});

const tagLineStyles = xcss({
	marginTop: 'space.050',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	color: 'color.text.subtlest',
	fontSize: '11px',
	lineHeight: '16px',
});

const tblzSwitcherTagLineStyles = xcss({
	color: 'color.text.subtlest',
	fontSize: '12px',
});

export const AppRecommendation: SurfaceModule<
	AppRecommendationProps & AppProvisionProps,
	AppRecommendationsProps,
	Actions
> = ({
	actionBus,
	name,
	description,
	journey = Journeys.DECIDE,
	productId,
	logo,
	appProvisioning,
	mutate,
	reasons,
	context,
	adControlsMenuLabel,
	adControlsMenuNotInterested,
	adControlsMenuWhyAmISeeingThis,
	recommendationReasonsModalTitle,
	recommendationReasonsModalExplanation,
	recommendationReasonsModalPrivacyPolicyLinkText,
	recommendationReasonsModalPrivacyPolicyLinkHref,
	recommendationReasonsModalAcceptButton,
}) => {
	const tblzSwitcherRoleBasedExperimentCohort = getExperimentCohort(context);

	const handleAppProvisioning = useCallback(
		async (journeyOverride?: Journey) => {
			try {
				const completionStatus = await appProvisioning({
					sourceComponent: 'atlassian-switcher',
					sourceContext: 'AppRecommendation',
					productId,
					journey: journeyOverride ?? journey,
					extraAnalyticsAttributes: {
						tblzSwitcherRoleBasedExperimentCohort,
					},
					extensions: context.sessionId
						? createRecommendationContextExtensions({
								entityId: productId,
								recommendationSessionId: context.sessionId,
							})
						: undefined,
				});

				if (completionStatus?.success === true) {
					mutate((currentConfiguration) => {
						return {
							...currentConfiguration,
							apps: currentConfiguration.apps?.filter((app) => app.productId !== productId),
						};
					});
				}
			} catch (error) {}
		},
		[
			appProvisioning,
			productId,
			journey,
			context.sessionId,
			tblzSwitcherRoleBasedExperimentCohort,
			mutate,
		],
	);

	const MenuTrigger = ({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
		<Pressable
			aria-label={formatStringMessage<'productName'>(adControlsMenuLabel, {
				productName: name,
			})}
			{...triggerProps}
			xcss={[commonButtonStyles, menuTriggerButtonStyles]}
			ref={triggerRef}
		>
			<MoreIcon primaryColor={token('color.icon')} label="" size="small" />
		</Pressable>
	);

	const messages = {
		recommendationReasonsModalTitle,
		recommendationReasonsModalExplanation,
		recommendationReasonsModalPrivacyPolicyLinkText,
		recommendationReasonsModalPrivacyPolicyLinkHref,
		recommendationReasonsModalAcceptButton,
	};

	return (
		<Box xcss={recommendationStyles}>
			<Pressable
				onClick={() => {
					const experimentValues = getExperimentValues(context, productId);
					handleAppProvisioning(experimentValues?.journeyOverride);
					actionBus?.dispatch('CLICK_APP_RECOMMENDATION', {
						productId,
						action: {
							action: 'clicked',
							actionSubject: 'listItem',
							actionSubjectId: 'product',
						},
						source: 'atlassianSwitcherDiscoverProductsSection',
						skipBenefitsPageExperimentCohort: experimentValues?.cohort,
						tblzSwitcherRoleBasedExperimentCohort,
					});
				}}
				xcss={[commonButtonStyles, recommendationButtonStyles]}
			>
				<Box as="span" xcss={recommendationBodyIconStyles}>
					<AppLogo
						src={logo}
						tblzSwitcherRoleBasedExperimentCohort={tblzSwitcherRoleBasedExperimentCohort}
					/>
				</Box>
				<Box xcss={recommendationBodyContentStyles}>
					{/* replace with specialised heading component */}
					<Box as="span" xcss={titleStyles}>
						{name}
					</Box>
					<Box
						as="span"
						xcss={[
							tagLineStyles,
							tblzSwitcherRoleBasedExperimentCohort === 'experiment' && tblzSwitcherTagLineStyles,
						]}
					>
						{getRecommendationDescription(
							description,
							productId,
							tblzSwitcherRoleBasedExperimentCohort,
						)}
					</Box>
				</Box>
			</Pressable>

			<Suspense fallback={<Box xcss={menuTriggerButtonStyles} />}>
				<AppRecommendationMenu
					{...messages}
					actionBus={actionBus}
					context={context}
					mutate={mutate}
					name={name}
					reasons={reasons}
					menu={[
						{
							title: adControlsMenuNotInterested,
							action: {
								actionType: 'DISMISS',
							},
						},
						{
							title: adControlsMenuWhyAmISeeingThis,
							action: {
								actionType: 'REASON',
							},
						},
					]}
					productId={productId}
					menuTrigger={MenuTrigger}
				/>
			</Suspense>
		</Box>
	);
};
